/**
 * Platform Configuration for Customer App
 *
 * Centralized configuration for building storefront URLs.
 * Uses environment variables to support different configurations for:
 * - Local development (localhost)
 * - Staging environment
 * - Production environment
 */

// Check if subdomain routing should be used in localhost
const shouldUseSubdomainInLocalhost = () => {
  return process.env.REACT_APP_USE_SUBDOMAIN_LOCALHOST !== 'false';
};

// Check if currently deployed on Vercel
const isVercelDeployment = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('vercel.app');
};

// Auto-detect the current platform domain
const getPlatformDomain = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  // For local development
  if (env === 'development') {
    return process.env.REACT_APP_PLATFORM_DOMAIN || 'localhost:3000';
  }

  // Auto-detect from current URL if on Vercel
  if (isVercelDeployment()) {
    // Already on customer app, so use current domain
    return window.location.hostname;
  }

  // For custom domain, extract from current hostname or use env var
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Check for environment-based subdomains (e.g., staging, production, dev)
    // For URLs like my.staging.userevstar.com, we want staging.userevstar.com as the platform domain
    const environmentKeywords = ['staging', 'production', 'prod', 'dev', 'development'];

    if (parts.length >= 3) {
      // Check if second-to-last part before TLD is an environment keyword
      // e.g., for my.staging.userevstar.com: parts = ['my', 'staging', 'userevstar', 'com']
      // parts[parts.length - 3] would be 'staging'
      const potentialEnv = parts[parts.length - 3];

      if (environmentKeywords.includes(potentialEnv)) {
        // Return last 3 parts (e.g., staging.userevstar.com)
        return parts.slice(-3).join('.');
      }
    }

    // Default: extract root domain (e.g., my.userevstar.com -> userevstar.com)
    if (parts.length >= 2) {
      return parts.slice(-2).join('.'); // Get last 2 parts (domain.com)
    }
  }

  // Fallback to env var or default
  return process.env.REACT_APP_PLATFORM_DOMAIN_PRODUCTION || 'userevstar.com';
};

/**
 * Build a storefront URL for a given store slug and optional path
 * @param {string} storeSlug - The store slug or merchant username
 * @param {string} path - Optional path (e.g., 'product/slug', 'shop', 'cart')
 * @returns {string} - The full storefront URL
 */
export const getStorefrontUrl = (storeSlug, path = '') => {
  if (!storeSlug) {
    console.error('getStorefrontUrl: storeSlug is required');
    return null;
  }

  const domain = getPlatformDomain();
  if (!domain) {
    console.error('getStorefrontUrl: REACT_APP_PLATFORM_DOMAIN not configured');
    return null;
  }

  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  const cleanPath = path.replace(/^\/+/, ''); // Remove leading slashes

  // For local development
  if (env === 'development') {
    // Use subdomain routing in localhost if enabled
    if (shouldUseSubdomainInLocalhost()) {
      const baseUrl = `http://${storeSlug}.${domain}`;
      return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
    }
    // Fallback to path-based routing
    const baseUrl = `http://${domain}/s/${storeSlug}`;
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
  }

  // For Vercel deployments: use path-based routing (Vercel doesn't support wildcard subdomains)
  if (isVercelDeployment()) {
    const baseUrl = `https://${domain}/s/${storeSlug}`;
    return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
  }

  // For custom domains: use subdomain routing
  const baseUrl = `https://${storeSlug}.${domain}`;
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
};

/**
 * Build a product page URL
 * @param {Object} product - Product object with slug and merchant data
 * @returns {string} - The full product page URL
 */
export const getProductUrl = (product) => {
  if (!product) {
    console.error('getProductUrl: product is required');
    return null;
  }

  if (!product.merchant) {
    console.error('getProductUrl: product must have merchant data');
    return null;
  }

  const storeSlug = product.merchant.store?.slug || product.merchant.username;
  if (!storeSlug) {
    console.error('getProductUrl: unable to determine store slug from product');
    return null;
  }

  return getStorefrontUrl(storeSlug, `product/${product.slug}`);
};

/**
 * Build a shop page URL
 * @param {string} storeSlug - The store slug
 * @returns {string} - The full shop page URL
 */
export const getShopUrl = (storeSlug) => {
  return getStorefrontUrl(storeSlug, 'shop');
};

/**
 * Build a cart page URL
 * @param {string} storeSlug - The store slug
 * @returns {string} - The full cart page URL
 */
export const getCartUrl = (storeSlug) => {
  return getStorefrontUrl(storeSlug, 'cart');
};

// Check if running in production environment
export const isProduction = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  return env === 'production';
};

// Export default config object
const platformConfig = {
  domain: getPlatformDomain(),
  getStorefrontUrl,
  getProductUrl,
  getShopUrl,
  getCartUrl,
  isProduction,
  environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development'
};

export default platformConfig;
